<template>
    <div class="dashboards-page">
        <breadcrumbs />
        <page-title :text="$t('page_titles.dashboards_page_title')" />
        <v-tabs v-if="isCorporate" v-model="tabs" class="mb-4">
            <v-tab>{{ $t('dashboards') }}</v-tab>
            <v-tab>{{ $t('calendar') }}</v-tab>
        </v-tabs>
        <template v-if="tabs === 0">
            <v-row class="dashboards-page__header">
                <v-col cols="12" md="4">
                    <order-service-daterange
                        :start-date-label="$t('create_date_from')"
                        :end-date-label="$t('create_date_to')"
                        :single-line="false"
                        :hide-details="true"
                        :_start-date="startDate"
                        :_end-date="endDate"
                        dense
                        @change-start-date="changeStartDate($event)"
                        @change-end-date="changeEndDate($event)"
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-select
                        v-model="serviceStatuses"
                        multiple
                        hide-details
                        :items="serviceStatusesItems"
                        :label="$t('services_status')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="service_status"
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-btn :block="$breakpoint.smAndDown" color="primary" @click="loadDashboards()">
                        {{ $t('refresh') }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-switch
                v-model="filterByServiceCreationDate"
                :label="$t('apply_filter_by_create_date_to_service')"
                hide-details
            />
            <v-row class="dashboards-page__body">
                <v-col v-if="summary" cols="12" sm="6" md="4">
                    <v-card
                        v-tooltip:bottom="$t('service_status_filter_note')"
                        class="dashboards-page__summary pa-4"
                        :min-height="$breakpoint.smAndDown ? '' : 496"
                    >
                        <v-card-title class="text-nobreak">{{ $t('dashboardsObject.summary') }}</v-card-title>
                        <div class="mt-8">
                            <div class="dashboards-page__summary-subtitle d-flex justify-center text-h4">
                                <span
                                    v-if="isTO1 || isTO2 || isAgency"
                                    v-html="$t('dashboardsObject.reservations', {prop: summary.reservationsCount})"
                                />
                                <span
                                    v-else-if="isCorporate"
                                    v-html="$t('dashboardsObject.trips', {prop: summary.reservationsCount})"
                                />
                            </div>
                            <div class="full-width d-flex justify-center mb-8 text-grey align-center">
                                <div class="text-h6">
                                    {{ summary.quotationsCount }} {{ $t('dashboardsObject.quotations') }}
                                </div>
                                <div class="ms-2 text-caption">
                                    ({{ conversionRate }}% {{ $t('dashboardsObject.conversion_rate') }})
                                </div>
                            </div>
                            <div class="dashboards-page__summary-text d-flex justify-space-around text-h5">
                                <div class="text-center">
                                    <span v-if="isCorporate">{{ $t('expenses').toLowerCase() }}</span>
                                    <span v-else>{{ $t('turnover').toLowerCase() }}</span>
                                    <div class="font-weight-bold text-primary">
                                        {{ $filters.priceFormat(summary.turnovers, summary.currency) }}
                                    </div>
                                </div>
                                <div v-if="isTO1 || isTO2" class="text-center">
                                    {{ $t('profit').toLowerCase() }}
                                    <div class="font-weight-bold text-error-darken-1">
                                        {{ $filters.priceFormat(summary.profit, summary.currency) }}
                                    </div>
                                </div>
                                <div v-if="isAgency && summary.bruttoCommission" class="text-center">
                                    {{ $t('commission').toLowerCase() }}
                                    <div class="font-weight-bold text-error-darken-1">
                                        {{ $filters.priceFormat(summary.bruttoCommission, summary.currency) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-col>
                <v-col v-if="reservationsByMonths" cols="12" sm="6" md="4">
                    <v-card class="dashboards-page__months pa-4" :min-height="$breakpoint.smAndDown ? '' : 496">
                        <v-card-title class="full-width">
                            <v-row align="center">
                                <v-col cols="12" xl="6">
                                    <span v-if="isTO1 || isTO2 || isAgency" class="text-nobreak">
                                        {{ $t('reservations_by_months') }}
                                    </span>
                                    <span v-else-if="isCorporate" class="text-nobreak">
                                        {{ $t('trips_by_months') }}
                                    </span>
                                </v-col>
                                <v-col cols="12" xl="6">
                                    <v-select
                                        v-model="topWidgetType"
                                        :items="topWidgetTypeItems"
                                        :label="$t('type')"
                                        clearable
                                        hide-details
                                        density="compact"
                                        variant="outlined"
                                        color="primary"
                                        data-cy="service_status"
                                        @update:model-value="loadDashboards"
                                    />
                                </v-col>
                            </v-row>
                        </v-card-title>
                        <bar-chart :data="reservationsByMonthsDataObject" class="chart-wrp" />
                    </v-card>
                </v-col>
                <v-col v-if="topDistributors" cols="12" sm="6" md="4">
                    <v-card class="dashboards-page__distributors pa-4" :min-height="$breakpoint.smAndDown ? '' : 496">
                        <v-card-title v-if="isTO1 || isTO2" class="text-nobreak">
                            {{ $t('top_clients_or_distributors') }}
                        </v-card-title>
                        <v-card-title v-if="isAgency" class="text-nobreak">{{ $t('top_clients') }}</v-card-title>
                        <table style="width: 100%">
                            <thead class="dashboards-page__table-header">
                                <tr>
                                    <th scope="col" class="font-weight-regular" />
                                    <th scope="col" class="font-weight-regular" />
                                    <th scope="col" class="font-weight-regular">
                                        <span v-if="showNumberOfBookingsLabel">{{ $t('number_of_bookings') }}</span>
                                    </th>
                                    <th
                                        scope="col"
                                        class="dashboards-page__agents-turnover font-weight-regular text-right"
                                    >
                                        {{ $t('turnover').toLowerCase() }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in topDistributors.tableRows" :key="item.id">
                                    <td scopre="row">
                                        <v-icon>{{ getIcon(item.rowType) }}</v-icon>
                                    </td>
                                    <td scopre="row">{{ item.name }}</td>
                                    <td scopre="row" class="text-right">{{ item.reservationsCount }}</td>
                                    <td scopre="row" class="text-right">
                                        {{ $filters.priceFormat(item.turnover, item.currency) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </v-card>
                </v-col>
                <v-col v-if="topAgents" cols="12" sm="6" md="4">
                    <v-card class="dashboards-page__agents pa-4" :min-height="$breakpoint.smAndDown ? '' : 496">
                        <v-card-title class="text-nobreak">{{ $t('top_agents') }}</v-card-title>
                        <table style="width: 100%">
                            <thead class="dashboards-page__table-header">
                                <tr>
                                    <th scope="col" class="font-weight-regular" />
                                    <th scope="col" class="font-weight-regular">
                                        <span v-if="showNumberOfBookingsLabel">{{ $t('number_of_bookings') }}</span>
                                    </th>
                                    <th
                                        scope="col"
                                        class="dashboards-page__agents-turnover font-weight-regular text-right"
                                    >
                                        {{ $t('turnover').toLowerCase() }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in topAgents.tableRows" :key="item.id">
                                    <td scope="row">{{ item.name }}</td>
                                    <td scope="row" class="text-right">{{ item.reservationsCount }}</td>
                                    <td scope="row" class="text-right">
                                        {{ $filters.priceFormat(item.turnover, item.currency) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </v-card>
                </v-col>
                <v-col v-if="topDestinations" cols="12" sm="6" md="4">
                    <v-card class="dashboards-page__destinations px-4" :min-height="$breakpoint.smAndDown ? '' : 496">
                        <v-card-title class="full-width">
                            <v-row align="center">
                                <v-col cols="12" md="6">
                                    <span class="text-nobreak">
                                        {{ $t('top_destinations') }}
                                    </span>
                                </v-col>
                                <v-col cols="12" lg="6">
                                    <v-select
                                        v-model="topWidgetType"
                                        :items="topWidgetTypeItems"
                                        :label="$t('type')"
                                        clearable
                                        hide-details
                                        density="compact"
                                        variant="outlined"
                                        color="primary"
                                        data-cy="service_status"
                                        @update:model-value="loadDashboards"
                                    />
                                </v-col>
                            </v-row>
                        </v-card-title>
                        <doughnut-chart :data="topDestinationsDataObject" class="chart-wrp" />
                    </v-card>
                </v-col>
                <v-col v-if="topDistributors" cols="12" sm="6" md="4">
                    <v-card class="dashboards-page__products px-4" :min-height="$breakpoint.smAndDown ? '' : 496">
                        <v-card-title class="full-width">
                            <v-row align="center">
                                <v-col cols="12" md="6">
                                    <span class="text-nobreak">
                                        {{ $t('top_products') }}
                                    </span>
                                </v-col>
                                <v-col cols="12" lg="6">
                                    <v-select
                                        v-model="topWidgetType"
                                        :items="topWidgetTypeItems"
                                        :label="$t('type')"
                                        clearable
                                        density="compact"
                                        hide-details
                                        variant="outlined"
                                        color="primary"
                                        data-cy="service_status"
                                        @update:model-value="loadDashboards"
                                    />
                                </v-col>
                            </v-row>
                        </v-card-title>
                        <doughnut-chart :data="topDistributorsDataObject" class="chart-wrp" />
                    </v-card>
                </v-col>
            </v-row>
        </template>
        <v-card v-else class="mt-8 mb-8 pa-4">
            <client-only>
                <orders-calendar
                    v-model="isLoadingOrders"
                    :orders="orders"
                    @request-created="loadOrders"
                    @change-focus-date="loadOrders($event)"
                />
            </client-only>
        </v-card>
    </div>
</template>

<script lang="ts">
    import {Vue, Component} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import BarChart from '@/components/snippets/charts/BarChart.vue'
    import DoughnutChart from '@/components/snippets/charts/DoughnutChart.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'

    @Component({
        components: {
            Breadcrumbs,
            OrderServiceDaterange,
            BarChart,
            DoughnutChart,
            OrdersCalendar: defineAsyncComponent(() => import('@/components/snippets/OrdersCalendar.vue')),
            PageTitle,
        },
    })
    export class DashboardsPage extends Vue {
        startDate = null
        endDate = null
        serviceStatuses = []
        filterByServiceCreationDate = false
        topWidgetType = null
        reservationsByMonths = null
        summary = null
        topAgents = null
        topDestinations = null
        topDistributors = null
        topProducts = null
        tabs = null
        isLoadingOrders = false
        orders = []

        mounted() {
            this.tabs = 0
            this.initDate()
            this.loadDashboards()
            if (this.isCorporate) this.loadOrders()
        }

        async loadDashboards() {
            try {
                const {reservationsByMonths, summary, topAgents, topDestinations, topDistributors, topProducts} =
                    await this.$api.dashboards.get({
                        startDate: this.startDate,
                        endDate: this.endDate,
                        ...(this.serviceStatuses?.length && {serviceStatuses: this.serviceStatuses}),
                        ...(this.topWidgetType && {topWidgetType: this.topWidgetType}),
                        filterByServiceCreationDate: this.filterByServiceCreationDate,
                    })
                this.reservationsByMonths = reservationsByMonths
                this.summary = summary
                this.topAgents = topAgents
                this.topDestinations = topDestinations
                this.topDistributors = topDistributors
                this.topProducts = topProducts
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async loadOrders(focus) {
            this.isLoadingOrders = true
            const date = focus ? this.$dateFns.parseISO(focus) : new Date()
            const from = this.$dateFns.format(this.$dateFns.subDays(this.$dateFns.startOfMonth(date), 6))
            const to = this.$dateFns.format(this.$dateFns.addDays(this.$dateFns.lastDayOfMonth(date), 6))
            const rq = {
                orderType: authStore.orderType,
                serviceStartDateFrom: from,
                serviceEndDateFrom: from,
                serviceStartDateTo: to,
                serviceEndDateTo: to,
            }

            try {
                const {orders} = await this.$api.orders.get(rq)
                this.orders = orders
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoadingOrders = false
            }
        }

        initDate() {
            const startDate = new Date()
            startDate.setMonth(0)
            startDate.setDate(1)
            startDate.setHours(0, 0, 0, 0)
            this.startDate = startDate
            this.endDate = new Date()
        }

        changeStartDate(e) {
            this.startDate = e
            this.loadDashboards()
        }

        changeEndDate(e) {
            this.endDate = e
            this.loadDashboards()
        }

        getIcon(type) {
            switch (type) {
                case 'AGENT':
                    return 'mdi-account-tie'
                case 'COMPANY':
                    return 'mdi-account-group-outline'
                case 'CORPORATE':
                    return 'mdi-account-supervisor'
                case 'PRIVATE_CLIENT':
                    return 'mdi-account'
                default:
                    return 'mdi-account'
            }
        }

        get reservationsByMonthsDataObject() {
            const labels = []
            const datasets = []
            const confirmed = [],
                total = [],
                lastConfirmed = [],
                lastTotal = []

            this.reservationsByMonths?.tableRows?.map(item => {
                labels.push(item.monthName)
                confirmed.push(item.confirmed)
                lastConfirmed.push(item.lastConfirmed)
                total.push(item.total)
                lastTotal.push(item.lastTotal)
            })
            datasets.push({
                backgroundColor: this.barCharnBackgroundColor.lastTotal,
                data: lastTotal,
                stack: 'Stack 0',
                token: 'dashboardsObject.total_last_year',
            })
            datasets.push({
                backgroundColor: this.barCharnBackgroundColor.lastConfirmed,
                data: lastConfirmed,
                stack: 'Stack 0',
                token: 'dashboardsObject.confirmed_last_year',
            })
            datasets.push({
                backgroundColor: this.barCharnBackgroundColor.total,
                data: total,
                stack: 'Stack 1',
                token: 'dashboardsObject.total_this_year',
            })
            datasets.push({
                backgroundColor: this.barCharnBackgroundColor.confirmed,
                data: confirmed,
                stack: 'Stack 1',
                token: 'dashboardsObject.confirmed_this_year',
            })

            return {
                labels,
                datasets,
            }
        }

        get topDestinationsDataObject() {
            const labels = []
            const data = []

            this.topDestinations.data?.map(({field1, field2}) => {
                labels.push(field1)
                data.push(field2)
            })

            return {
                labels,
                datasets: [
                    {
                        backgroundColor: this.backgroundColor,
                        data,
                    },
                ],
            }
        }

        get topDistributorsDataObject() {
            const labels = []
            const data = []

            this.topProducts.data?.map(({field1, field2}) => {
                labels.push(field1)
                data.push(field2)
            })

            return {
                labels,
                datasets: [
                    {
                        backgroundColor: this.backgroundColor,
                        data,
                    },
                ],
            }
        }

        get backgroundColor() {
            return [
                '#3366cc',
                '#DC3912',
                '#FF9900',
                '#109618',
                '#990099',
                '#0099C6',
                '#DD4477',
                '#66aa00',
                '#B82E2E',
                '#41B883',
            ]
        }

        get barCharnBackgroundColor() {
            return {
                lastTotal: 'rgba(86, 86, 86, 0.5)',
                lastConfirmed: 'rgba(0, 130, 0, 0.5)',
                total: 'rgb(86, 86, 86)',
                confirmed: 'rgb(0, 130, 0)',
            }
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get serviceStatusesItems() {
            return [
                {
                    title: this.$t('bookingStatus.CONFIRMED'),
                    value: 'Confirmed',
                },
                {
                    title: this.$t('bookingStatus.CONFIRMATION_PENDING'),
                    value: 'Confirmation pending',
                },
                {
                    title: this.$t('bookingStatus.CANCELLATION_PENDING'),
                    value: 'Cancellation pending',
                },
                {
                    title: this.$t('bookingStatus.MODIFICATION_PENDING'),
                    value: 'Modification pending',
                },
                {
                    title: this.$t('bookingStatus.CANCELED'),
                    value: 'Canceled',
                },
                {
                    title: this.$t('bookingStatus.REJECTED'),
                    value: 'Rejected',
                },
                {
                    title: this.$t('bookingStatus.ERROR'),
                    value: 'Error',
                },
                {
                    title: this.$t('bookingStatus.QUOTE'),
                    value: 'Quote',
                },
                {
                    title: this.$t('bookingStatus.PRECONFIRMED'),
                    value: 'PreConfirmed',
                },
                {
                    title: this.$t('bookingStatus.PENDING_TICKETING'),
                    value: 'Pending ticketing',
                },
                {
                    title: this.$t('bookingStatus.ESTIMATED'),
                    value: 'Estimated',
                },
            ]
        }

        get topWidgetTypeItems() {
            return ['TURNOVER', 'INCOME', 'QUANTITY'].map(value => ({
                value,
                title: this.$t(`dashboard_top_widget_type.${value}`),
            }))
        }

        get conversionRate() {
            if (!this.summary) {
                return null
            }

            return (
                (this.summary?.reservationsCount / (this.summary?.reservationsCount + this.summary?.quotationsCount)) *
                100
            ).toFixed(2)
        }

        get showNumberOfBookingsLabel() {
            return false
        }
    }

    export default DashboardsPage
</script>

<style lang="scss" scoped>
    .chart-wrp {
        max-height: 400px;
    }
</style>
